// Sliding-puzzle engine (MVP). UI-agnostic, SOLID/SRP.
//
// Board model
// - size = N (3..8)
// - board = Array<N*N> with values 0..N*N-1 (last value is the "empty").
//   Solved iff board[i] === i for all i.
//
// API (stable)
// - start({ size?, difficulty? })
// - reset()
// - reshuffle({ size?, difficulty? })
// - canMoveTile(index)
// - moveTile(index) -> boolean
// - giveUp()
// - getElapsedMs()               // on-demand; avoids re-renders
//
// Shuffling (random-walk):
// - Start from solved and apply K legal moves (no immediate backtrack).
// - K comes from a fixed table per size/difficulty (tunable by UX).
// - Then apply a small fixed “jiggle” (4 legal moves) to avoid trivial 1–2 move cases.
//   Rationale: 4 es un equilibrio práctico entre simplicidad y alejar la solución,
//   sin sobrecoste ni heurísticas. No bucles, no retries; siempre resoluble.
//
// Notes
// - Image/routing agnostic.
// - Pure helpers are kept separate for testability.

import { useCallback, useMemo, useRef, useState } from 'react'
import { DIFFICULTY_TO_SIZE } from '../constants/puzzle'
import { clampSize } from '../logic/clampSize'
import { makeSolved } from '../logic/makeSolved'
import { isSolvedBoard } from '../logic/isSolvedBoard'
import { shuffleDepthFor } from '../logic/shuffleDepthFor'
import { indexToRC } from '../logic/indexToRC'
import { canMoveIn } from '../logic/canMoveIn'
import { randomWalkShuffle } from '../logic/randomWalkShuffle'

// ----- Hook ---------------------------------------------------------------

export function usePuzzleEngine () {
  const [size, setSize] = useState(3)
  const [board, setBoard] = useState(() => makeSolved(3))
  const [moves, setMoves] = useState(0)
  const [isStarted, setIsStarted] = useState(false)
  const startedAtRef = useRef(null)

  const emptyValue = useMemo(() => size * size - 1, [size])
  const emptyIndex = useMemo(() => board.indexOf(emptyValue), [board, emptyValue])
  const isSolved = useMemo(() => isSolvedBoard(board), [board])

  const canMoveTile = useCallback((index) => {
    return canMoveIn(board, index, size, emptyValue)
  }, [board, size, emptyValue])

  const moveTile = useCallback((index) => {
    let moved = false
    setBoard(prev => {
      if (!canMoveIn(prev, index, size, emptyValue)) return prev
      const e = prev.indexOf(emptyValue)
      const next = prev.slice()
      ;[next[e], next[index]] = [next[index], next[e]]
      moved = true
      return next
    })
    if (moved) setMoves(m => m + 1)
    return moved
  }, [size, emptyValue])

  const start = useCallback((opts = {}) => {
    const chosenSize = typeof opts.size === 'number'
      ? clampSize(opts.size)
      : DIFFICULTY_TO_SIZE[opts.difficulty] ?? size

    const difficulty = opts.difficulty ?? 'easy'
    const depth = shuffleDepthFor(chosenSize, difficulty)

    setSize(chosenSize)
    setBoard(randomWalkShuffle(chosenSize, depth))
    setMoves(0)
    setIsStarted(true)
    startedAtRef.current = Date.now()
  }, [size])

  const reset = useCallback(() => {
    setBoard(makeSolved(size))
    setMoves(0)
    setIsStarted(false)
    startedAtRef.current = null
  }, [size])

  // Symmetric with start(): accepts optional { size, difficulty }
  const reshuffle = useCallback((opts = {}) => {
    const chosenSize = typeof opts.size === 'number' ? clampSize(opts.size) : size
    const difficulty = opts.difficulty ?? 'easy'
    const depth = shuffleDepthFor(chosenSize, difficulty)

    setSize(chosenSize)
    setBoard(randomWalkShuffle(chosenSize, depth))
    setMoves(0)
    setIsStarted(true)
    startedAtRef.current = Date.now()
  }, [size])

  const giveUp = useCallback(() => {
    setBoard(makeSolved(size))
    setIsStarted(false)
  }, [size])

  // Getter for elapsed time (on-demand; no re-renders)
  const getElapsedMs = useCallback(() => {
    if (!isStarted || !startedAtRef.current) return 0
    return Date.now() - startedAtRef.current
  }, [isStarted])

  return {
    // state
    board,
    size,
    moves,
    isStarted,
    isSolved,
    emptyIndex,

    // actions
    start, // start({ size?, difficulty? })
    reset,
    reshuffle, // reshuffle({ size?, difficulty? })
    moveTile,
    canMoveTile,
    giveUp,

    // helpers
    setSize: (n) => setSize(clampSize(n)),
    indexToRC: (i) => indexToRC(i, size),
    getElapsedMs
  }
}
